import React from 'react';
import { ChevronLeftIcon, ShieldCheckIcon, ArrowRightIcon, TrendingUpIcon, CalculatorIcon, TagIcon, SparklesIcon } from 'lucide-react';
import { RoofTypeConfig } from '../App';
interface CompetitorComparisonProps {
  squares: number;
  sellingPrice: number;
  roofConfig: RoofTypeConfig;
  onBack: () => void;
}
export function CompetitorComparison({
  squares,
  sellingPrice,
  roofConfig,
  onBack
}: CompetitorComparisonProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  const normalTotal = roofConfig.normalPricePerSq * squares;
  const discountedTotal = sellingPrice * squares;
  const discountSavings = normalTotal - discountedTotal;
  const competitors = roofConfig.competitors;
  const maxBarValue = Math.max(...competitors.map(c => c.pricePerSq)) * squares;
  return <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-20" data-id="element-125">
      {/* Header */}
      <header className="bg-[#1B5E3B] text-white py-6 shadow-md" data-id="element-126">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between" data-id="element-127">
          <div className="flex items-center space-x-3" data-id="element-128">
            <img src="https://cdn.magicpatterns.com/uploads/pKiHu7huFTB332wjVWDXnG/rl1.png" alt="Relentless Construction Logo" className="h-10 w-10 rounded-md object-cover" data-id="element-129" />
            <h1 className="text-2xl font-bold tracking-tight" data-id="element-130">
              Relentless Construction
            </h1>
          </div>
          <div className="hidden sm:flex items-center space-x-2 text-green-100 text-sm font-medium" data-id="element-131">
            <CalculatorIcon className="h-4 w-4" data-id="element-132" />
            <span data-id="element-133">Pricing Dashboard</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-134">
        {/* Back Button */}
        <button onClick={onBack} className="inline-flex items-center text-sm font-medium text-[#1B5E3B] hover:text-[#113f27] transition-colors group" data-id="element-135">
          <ChevronLeftIcon className="h-4 w-4 mr-1 transition-transform group-hover:-translate-x-0.5" data-id="element-136" />
          Back to Calculator
        </button>

        {/* Page Title */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8" data-id="element-137">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-138">
            <div className="flex items-center space-x-3" data-id="element-139">
              <div className="bg-orange-100 p-2 rounded-lg" data-id="element-140">
                <TrendingUpIcon className="h-6 w-6 text-orange-700" data-id="element-141" />
              </div>
              <div data-id="element-142">
                <h2 className="text-2xl font-bold text-slate-800" data-id="element-143">
                  Competitor Comparison
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-144">
                  Total cost to homeowner for a {squares}-square{' '}
                  {roofConfig.label} roof
                </p>
              </div>
            </div>
            {roofConfig.warranty && <div className="inline-flex items-center bg-amber-50 text-amber-800 text-sm font-semibold px-4 py-2 rounded-full border border-amber-200" data-id="element-145">
                <ShieldCheckIcon className="h-4 w-4 mr-2" data-id="element-146" />
                {roofConfig.warranty}
              </div>}
          </div>
        </div>

        {/* Comparison Bars */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8" data-id="element-147">
          <div className="space-y-8" data-id="element-148">
            {/* Relentless — Normal Pricing */}
            <div data-id="element-149">
              <div className="flex justify-between items-center mb-2" data-id="element-150">
                <span className="text-slate-500 font-semibold flex items-center text-sm" data-id="element-151">
                  <TagIcon className="h-4 w-4 mr-1.5" data-id="element-152" />
                  Relentless Construction — Normal Pricing
                </span>
                <span className="text-slate-500 font-bold text-lg line-through" data-id="element-153">
                  {formatCurrency(normalTotal)}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-8 overflow-hidden flex items-center shadow-inner" data-id="element-154">
                <div className="bg-slate-400 h-full rounded-full transition-all duration-500 ease-out" style={{
                width: `${normalTotal / maxBarValue * 100}%`
              }} data-id="element-155"></div>
              </div>
            </div>

            {/* Relentless — Advertising Home Discounted Price */}
            <div data-id="element-156">
              <div className="flex justify-between items-center mb-2" data-id="element-157">
                <span className="text-[#1B5E3B] font-semibold flex items-center text-sm" data-id="element-158">
                  <SparklesIcon className="h-4 w-4 mr-1.5" data-id="element-159" />
                  Relentless Construction — Advertising Home Discounted Price
                </span>
                <span className="text-[#113f27] font-bold text-lg" data-id="element-160">
                  {formatCurrency(discountedTotal)}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-8 overflow-hidden flex items-center shadow-inner" data-id="element-161">
                <div className="bg-gradient-to-r from-[#2a8750] to-[#1B5E3B] h-full rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-3" style={{
                width: `${discountedTotal / maxBarValue * 100}%`
              }} data-id="element-162">
                  <span className="text-xs font-bold text-white drop-shadow-sm" data-id="element-163">
                    BEST VALUE
                  </span>
                </div>
              </div>
              {discountSavings > 0 && <div className="mt-2 text-sm font-medium text-green-600 flex items-center" data-id="element-164">
                  <ArrowRightIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-165" />
                  You save {formatCurrency(discountSavings)} off our normal
                  price
                </div>}
            </div>

            {/* Competitors */}
            {competitors.map(comp => {
            const compTotal = comp.pricePerSq * squares;
            const percentage = compTotal / maxBarValue * 100;
            const savings = compTotal - discountedTotal;
            return <div key={comp.name} data-id="element-166">
                  <div className="flex justify-between items-center mb-2" data-id="element-167">
                    <span className="text-slate-600 font-semibold text-sm" data-id="element-168">
                      {comp.name}
                    </span>
                    <span className="text-slate-800 font-bold text-lg" data-id="element-169">
                      {formatCurrency(compTotal)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-8 overflow-hidden flex items-center shadow-inner" data-id="element-170">
                    <div className={`${comp.color} h-full rounded-full transition-all duration-500 ease-out`} style={{
                  width: `${percentage}%`
                }} data-id="element-171"></div>
                  </div>
                  {savings > 0 && <div className="mt-2 text-sm font-medium text-green-600 flex items-center" data-id="element-172">
                      <ArrowRightIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-173" />
                      You save {formatCurrency(savings)} with Relentless
                    </div>}
                </div>;
          })}
          </div>

          {/* Savings Summary Cards */}
          <div className={`grid grid-cols-1 sm:grid-cols-${competitors.length + 1} gap-4 mt-10`} data-id="element-174">
            <div className="bg-green-50 rounded-xl p-5 border border-green-100 text-center" data-id="element-175">
              <p className="text-sm text-green-700 font-medium mb-1" data-id="element-176">
                Savings vs Normal Price
              </p>
              <p className="text-3xl font-bold text-[#1B5E3B]" data-id="element-177">
                {formatCurrency(discountSavings)}
              </p>
            </div>
            {competitors.map(comp => <div key={comp.name} className="bg-green-50 rounded-xl p-5 border border-green-100 text-center" data-id="element-178">
                <p className="text-sm text-green-700 font-medium mb-1" data-id="element-179">
                  Savings vs {comp.name}
                </p>
                <p className="text-3xl font-bold text-[#1B5E3B]" data-id="element-180">
                  {formatCurrency(comp.pricePerSq * squares - discountedTotal)}
                </p>
              </div>)}
          </div>
        </div>
      </main>
    </div>;
}